// DevHub UI kit — modifier-click-safe navigation.
//
// The app navigated via `onClick={() => navigate(to)}` on non-anchor elements
// (divs/buttons/odx-buttons). Browsers only honour "open in a new tab" — Cmd/Ctrl
// +click, middle-click, and the right-click menu — on REAL <a href> anchors, so
// none of those worked. This hook returns props for a real link that still
// single-page-navigates on a plain left click but lets the browser take over for
// modifier / middle / right clicks.
//
// Spread the result onto an <a> (replacing a `role="button"` div) or an
// <odx-button> (which renders a proxy anchor when given `href`):
//
//   const link = useLink();
//   <a {...link(to)} className="my-card">…</a>
//   <odx-button variant="ghost" {...link(to)}>Docs</odx-button>
//
// A plain click is intercepted (preventDefault + client-side navigate); a click
// with a modifier key, or a middle/right click, is left to the browser so the URL
// opens in a new tab/window. Nested interactive controls (e.g. a card's star
// button) must still call stopPropagation so they don't trigger the link.
import { useCallback } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

export interface LinkProps {
  href: string;
  onClick: (e: MouseEvent) => void;
}

export function useLink(): (to: string) => LinkProps {
  const navigate = useNavigate();
  return useCallback(
    (to: string): LinkProps => ({
      href: to,
      onClick: (e: MouseEvent) => {
        // Something already handled it (e.g. a nested button), or the user wants a
        // new tab/window — let the browser follow the href natively.
        if (
          e.defaultPrevented ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey ||
          e.button !== 0
        ) {
          return;
        }
        e.preventDefault();
        navigate(to);
      },
    }),
    [navigate],
  );
}
